/* eslint-disable jsx-a11y/no-static-element-interactions -- onClick handlers on div wrappers for event propagation control */
import React, { useEffect, useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format, addDays } from 'date-fns';
import moment from 'moment';
import { Spin, Table, Empty } from 'antd-v5';
import { useQuery } from 'react-query';
import { LoadingOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button } from 'antd-v5';
import { sourceCalc } from './logicFunctions';
import { getRequest, postRequest } from '../../../../core/apiRequests';
import { getTablePaginationData, notificationSuccess } from '../../../../core/actions';
import MessageConfigToday from './MessageConfigToday';
import { associateNameFormat } from '../../../../utils';
import SnoozeModal from '../Reminders/children/SnoozeModal';
import ReminderModal from '../../../Layout/Modals/EditReminderModal';
import DropDownSelect from '../../../Layout/DropdownSelect';
import PopConfirm from '../../../Layout/PopConfirm';
import '../today.css';

const TodayAutoReminders = props => {
  const [isShown, setIsShown] = useState('');
  const today = format(new Date(), 'yyyyMMdd');
  const tomorrowNoFormat = addDays(new Date(), 1);
  const tomorrow = format(tomorrowNoFormat, 'yyyyMMdd');
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [editReminderModal, setEditReminderModal] = useState(false);
  const [checkCustomReminder, setCheckCustomReminder] = useState(false);
  const [assignPopup, setAssignPopup] = useState(false);
  const [dismissPopup, setDismissPopup] = useState(false);
  const [associate, setAssociate] = useState();
  const [selectedAction, setSelectedAction] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [singleRem, setSingleRem] = useState();

  useEffect(() => {
    if (selectedAction.length === 1) {
      const [action] = selectedAction;
      if (
        !action.life_event_id &&
        !action.tag_action_config_id &&
        !action.sales_opp_id &&
        !action.custom_event_type_id &&
        !action.sales_action_config_id
      ) {
        setCheckCustomReminder(true);
      }
    } else {
      setCheckCustomReminder(false);
    }
  }, [selectedAction]);

  const rowClickHandler = item => {
    setSingleRem([item]);
  };

  const {
    data,
    refetch: refetchTodayActions,
    isFetching: isTodayActionsLoading,
  } = useQuery(
    ['today-actions', props.pagination],
    async () => {
      const { sortColumn, ascendingColumn } = props.pagination;
      const reqObj = {
        loadingStatusOff: true,
        params: ['today', 'actions'],
        query: {
          sortColumn,
          ascending: ascendingColumn,
        },
      };
      const res = await getRequest(reqObj);
      return res;
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
      enabled: !!props.auth.merchantId,
    },
  );

  // 04/05 THIS CURRENTLY USES CLIENT STORE_ID. IT NEEDS TO EVENTUALLY USE THE ASSOCIATE STORE ID
  // associate store_id with more than one associated store is 0. We do not have a way of knowing default store yet for associates.
  const saveSelected = () => {
    const selected = selectedAction;
    const reqObj = {
      params: ['today', props.auth.merchantId, 'save'],
      data: { selected },
      query: {
        storeId: props.auth.storeId,
        associateId: props.auth.userId,
        createdBy: `${props.auth.firstName || ''} ${props.auth.lastName || ''}`,
      },
    };

    (async () => {
      const res = await postRequest(reqObj);
      if (res) {
        setSelectedAction([]);
        setSelectedRowKeys([]);
        refetchTodayActions();
        notificationSuccess('Reminder(s) Complete!');
      }
    })();
  };

  const expiresOn = (todayDate, expiresOnDate) => {
    const a = moment(todayDate, 'YYYYMMDD');
    const b = moment(expiresOnDate, 'YYYYMMDD');
    const diffDays = b.diff(a, 'days');
    return diffDays;
  };

  useEffect(() => {
    if (!props?.messaging?.client?.reminderId) {
      refetchTodayActions();
    }
  }, [props?.messaging?.client?.reminderId]);

  const columns = useMemo(
    () => [
      {
        title: 'Due Date',
        dataIndex: 'due_date',
        key: 'due_date',
        render: (t, record) => {
          if (t) {
            if (parseInt(today) === record.expires_on_date) {
              return (
                <div className="flex-col-left">
                  {moment(t, 'YYYYMMDD').format('MM/DD/YYYY')}
                  <span className="fs-12 fw-400 gray">Expires today</span>
                </div>
              );
            }
            if (parseInt(tomorrow) === record.expires_on_date) {
              return (
                <div className="flex-col-left">
                  {moment(t, 'YYYYMMDD').format('MM/DD/YYYY')}
                  <span className="fs-12 fw-400 gray">Expires tomorrow</span>
                </div>
              );
            }
            if (record.expires_on_date) {
              return (
                <div className="flex-col-left">
                  {moment(t, 'YYYYMMDD').format('MM/DD/YYYY')}
                  <span className="fs-12 fw-400 gray">Expires in {expiresOn(today, record.expires_on_date)} days</span>
                </div>
              );
            }
            return moment(t, 'YYYYMMDD').format('MM/DD/YYYY');
          }
          return null;
        },
      },
      {
        title: 'Client Name',
        dataIndex: 'name',
        key: 'name',
        render: (text, record) => <Link to={`/Clients?client=${record.client_id}`}>{text}</Link>,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        render: (_, record) => sourceCalc(record),
      },
      {
        title: 'Reassign',
        dataIndex: 'reassigned_by',
        key: 'reassigned_by',
        align: 'center',
        render: (_, record) => (
          <div className="align-center" style={{ maxHeight: '34px' }}>
            <FontAwesomeIcon
              icon={['far', 'pencil']}
              onClick={e => {
                e.stopPropagation();
                setAssignPopup(record);
              }}
              className="pointer gray"
            />
          </div>
        ),
      },
      {
        title: 'Message',
        dataIndex: 'message',
        key: 'message',
        align: 'center',
        render: (_, record) => (
          <div className="align-center pos-rel" style={{ maxHeight: '34px' }} onClick={e => e.stopPropagation()}>
            <MessageConfigToday
              keyVar={Math.floor(Math.random() * record.id)}
              msgObj={record}
              setShowPopConfirm={props.setShowPopConfirm}
              initiateTemplates={props.initiateTemplates}
              setIsShown={setIsShown}
              isShown={isShown}
              setSelectedClient={props.setSelectedClient}
            />
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render: (_, record) => {
          const now = moment(new Date());
          if (!record.complete_date) {
            if (moment(now).isAfter(moment(record.due_date, 'YYYYMMDD').add(1, 'day'))) {
              return <span className="today-status-badge overdue">OVERDUE</span>;
            }
            return <span className="today-status-badge active">ACTIVE</span>;
          }
          return null;
        },
      },
    ],
    [today, tomorrow, props.setShowPopConfirm, props.initiateTemplates, props.setSelectedClient, isShown],
  );

  const rowSelection = {
    selectedRowKeys,
    columnWidth: 48, // Match Appointments table checkbox column width
    onChange: (newSelectedRowKeys, newSelectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedAction(newSelectedRows);
    },
  };

  const assignAssociate = () => {
    setAssignPopup(false);
    let reqObj;
    if (singleRem) {
      reqObj = {
        params: ['today', props.auth.merchantId, 'assignAssociate'],
        data: { selectedAction: singleRem },
        query: {
          assignedAssociate: associate,
          storeId: props.auth.storeId,
          associateId: props.auth.userId,
        },
      };
    } else {
      reqObj = {
        params: ['today', props.auth.merchantId, 'assignAssociate'],
        data: { selectedAction },
        query: {
          assignedAssociate: associate,
          storeId: props.auth.storeId,
          associateId: props.auth.userId,
        },
      };
    }
    (async () => {
      const res = await postRequest(reqObj);
      if (res) {
        setSelectedAction([]);
        setSelectedRowKeys([]);
        refetchTodayActions();
        setSingleRem([]);
        notificationSuccess('Reminder(s) Assigned!');
        setAssociate(props.auth.userId);
      }
    })();
  };

  const dismissSelected = () => {
    const reqObj = {
      params: ['today', props.auth.merchantId, 'dismissSelected'],
      query: {
        associateName: associateNameFormat(props.auth.firstName, props.auth.lastName),
        associateId: props.auth.userId,
      },
      data: { selectedAction },
    };
    (async () => {
      const responseObj = await postRequest(reqObj);
      if (responseObj) {
        setSelectedAction([]);
        setSelectedRowKeys([]);
        setDismissPopup(false);
        refetchTodayActions();
        notificationSuccess('Reminder(s) Dismissed!');
      }
    })();
  };

  return (
    <div className="m-btm-35">
      <div className="flex-row items-center justify-between m-btm-10">
        <span className="fs-15 fw-500">Reminders</span>
        {selectedAction.length > 0 ? (
          <div className="cb-link-btn-group cb-link-btn-group--wide">
            {checkCustomReminder && (
              <Button type="link" className="cb-link-btn-action" onClick={() => setEditReminderModal(true)}>
                <FontAwesomeIcon icon={['far', 'pencil']} />
                Edit
              </Button>
            )}
            <Button type="link" className="cb-link-btn-action" onClick={() => setAssignPopup(true)}>
              <FontAwesomeIcon icon={['far', 'user']} />
              Assign Associate
            </Button>
            <Button type="link" className="cb-link-btn-action cb-link-btn-action--icon-lg" onClick={saveSelected}>
              <FontAwesomeIcon icon={['far', 'check-circle']} />
              Complete
            </Button>
            <Button type="link" className="cb-link-btn-action" onClick={() => setDismissPopup(true)}>
              <FontAwesomeIcon icon={['far', 'eraser']} />
              Dismiss
            </Button>
            <Button type="link" className="cb-link-btn-action" onClick={() => setShowSnoozeModal(true)}>
              <FontAwesomeIcon icon="fa-sharp fa-regular fa-alarm-clock" />
              Snooze
            </Button>
          </div>
        ) : (
          <Link to="/all-reminders" id="ViewAllReminders">
            <Button type="link" className="cb-link-btn">
              View All
            </Button>
          </Link>
        )}
      </div>

      {isTodayActionsLoading ? (
        <div className="w-100-P flex-col-center m-top-10">
          <Spin tip="Loading..." size="large" indicator={<LoadingOutlined spin />}>
            <div
              style={{
                padding: 50,
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: 8,
              }}
            />
          </Spin>
        </div>
      ) : (
        <>
          <Table
            className="today-table"
            columns={columns}
            dataSource={data}
            rowKey={record => record.id}
            pagination={false}
            rowSelection={rowSelection}
            onRow={record => ({
              onClick: () => rowClickHandler(record),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Reminders" />,
            }}
          />
        </>
      )}

      {editReminderModal && (
        <ReminderModal
          open={editReminderModal}
          reminder={selectedAction?.[0] ?? {}}
          allAssoc={props.allAssoc}
          onClose={() => setEditReminderModal(false)}
          setReload={() => {
            setSelectedAction([]);
            setSelectedRowKeys([]);
            refetchTodayActions();
          }}
        />
      )}
      <SnoozeModal
        openModal={showSnoozeModal}
        setOpenModal={setShowSnoozeModal}
        merchantId={props.auth.merchantId}
        actions={selectedAction}
        refresh={() => {
          setSelectedAction([]);
          setSelectedRowKeys([]);
          refetchTodayActions();
        }}
      />

      {assignPopup ? (
        <PopConfirm
          popStyle={{
            width: '380px',
          }}
          confirmButtonText="SAVE"
          cancelButtonText="CANCEL"
          confirm="Assign Associate"
          description={[
            <div key="primary" className="align-left m-top-10" style={{ height: '175px' }}>
              <span className="dropDownDescriptionText">Assign an Associate</span>
              <DropDownSelect
                classProps="mq-w-200 w-295 align-left m-top-8"
                options={props.allAssoc.map(asc => ({
                  label: asc.fullName ? asc.fullName : `${asc.first_name || ''} ${asc.last_name || ''}`,
                  value: asc.id,
                }))}
                placeholder="Select an Associate..."
                value={associate}
                onChange={obj => {
                  setAssociate(obj.value);
                }}
              />
            </div>,
          ]}
          noButtonDisable
          onCancel={() => setAssignPopup(false)}
          onConfirm={assignAssociate}
        />
      ) : null}
      {dismissPopup ? (
        <PopConfirm
          popStyle={{
            width: '380px',
          }}
          customHeaderContent="fs-16 align-center"
          confirmButtonText="Dismiss"
          cancelButtonText="Cancel"
          confirm="Dismiss Selected Reminders"
          description="These reminders will be dismissed from your list.  If you continue, these reminders will be permanently removed from your profile."
          noButtonDisable
          onCancel={() => setDismissPopup(false)}
          onConfirm={dismissSelected}
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  messaging: state.messaging,
  pagination: getTablePaginationData(state, 'TODAY_AUTO_REMINDERS'),
});
export default connect(mapStateToProps, {})(TodayAutoReminders);
